import { useEffect, useState } from 'react';
import type { School } from '@gov-nx/api';
import {
	inforAboutStudyingQuery,
	useAxiosInstance,
	usePoMutation,
	usePoQuery,
} from '@gov-nx/api';
import type { Optional, TypeOfSchool } from '@gov-nx/core/types';
import { SchoolCategoryCode } from '@gov-nx/core/types';

export interface SchoolSearchAutocompleteProps {
	type: TypeOfSchool;
}

export const useSchoolSearchAutocomplete = ({
	type,
}: SchoolSearchAutocompleteProps) => {
	const [text, setText] = useState<Optional<string>>(undefined);
	const { axiosInstance } = useAxiosInstance();

	// Get rid of stored text on type change
	useEffect(() => setText(undefined), [type]);

	const mainQuery = usePoQuery<{ seznam: School[] }>({
		queryKey: ['school-autocomplete', text, type],
		queryFn: async () => {
			const response = await inforAboutStudyingQuery(axiosInstance, {
				kodKategorie: SchoolCategoryCode[type] ?? undefined,
				text: text,
			});
			return response.data;
		},
		enabled: text ? text?.length > 2 : false,
		retry: 0,
		refetchOnWindowFocus: false,
	});

	const mutation = usePoMutation({
		mutationFn: async (value: string) => {
			const response = await inforAboutStudyingQuery(axiosInstance, {
				kodKategorie: SchoolCategoryCode[type] ?? undefined,
				text: value,
			});
			return response.data;
		},
	});

	return {
		isLoading: mainQuery.isLoading,
		isFetching: mainQuery.isFetching,
		schools: mainQuery.data?.seznam || [],
		setText,
		searchCallback: async (value: string): Promise<School[]> => {
			const result = await mutation.mutateAsync(value);
			return result.seznam;
		},
	};
};
